/**
 * MappingContextPanel — 3-tab right panel for mapping mode.
 *
 * Tabs: Chat | Logic | Lineage  (Readiness findings live in the left Review pane)
 *
 * Replaces the separate ChatPanel (agent mode) and PreviewPanel (mapping editor)
 * with a unified context panel that provides full vertical height to each tab.
 *
 * Layout position: Cell 2 (right column, 400px) in the 2×2 grid, same slot
 * that ChatPanel occupies in agent mode.
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Badge,
  Box,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Code as LogicIcon,
  BugReport as DiagIcon,
  ArrowBack as BackIcon,
  ChevronRight as CollapseIcon,
} from '@mui/icons-material';
import ChatPanel from './ChatPanel';
import type { MappingContext } from './ChatPanel';
import DiagnosticsTab from './DiagnosticsTab';
import { LogicEditorTab } from '../../canvas/inspector-tabs/LogicEditorTab';
import type { LogicEditorTabProps } from '../../canvas/inspector-tabs/LogicEditorTab';
import type { DiagnosticsTabProps } from './DiagnosticsTab';
import type { MappingResponse } from '../../services/types';
import type { WorkspaceStreamState } from '../../hooks/useWorkspaceSession';
import type { WorkspaceOperation } from '../../services/workspaceApi';
import type { Suggestion } from './ContextualSuggestions';
import type { PipelineNode } from './DAGCanvas';

// ── Types ────────────────────────────────────────────────────────────

type ContextTab = 'chat' | 'logic' | 'diagnostics';

export interface MappingContextPanelProps {
  // Chat tab props
  sessionId: string;
  batchFileName?: string;
  selectedNode: PipelineNode | null;
  onDeselectNode: () => void;
  operations: WorkspaceOperation[];
  stream: WorkspaceStreamState;
  pendingMessage?: string | null;
  onPendingMessageConsumed?: () => void;
  isBusy: boolean;
  contextualSuggestions: Suggestion[];

  // Logic tab props
  logicProps: Omit<LogicEditorTabProps, 'onAskAboutMapping'>;

  // Lineage tab props (previously "Diagnostics"; Health section was
  // removed in the Readiness rollout — findings live in the left
  // Review pane and toolbar Readiness chip now)
  diagnosticsProps: DiagnosticsTabProps;

  // ``healthIssueCount`` was used by the now-removed Health badge on
  // the right-rail tab. Kept as a deprecated no-op for one release
  // so existing callers compile; deletes once they migrate to
  // reading findings from the Readiness endpoint.
  /** @deprecated no longer rendered; use Readiness findings instead */
  healthIssueCount?: number;

  // Mapping feedback
  onMappingFeedbackSuccess?: () => void;

  /** When true, mapping has been triggered (hides chat starters, same as workspace). */
  mappingActive?: boolean;

  // Mode navigation
  onSwitchToDagMode: () => void;

  // Collapsed state
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

// ── Component ────────────────────────────────────────────────────────

const MappingContextPanel: React.FC<MappingContextPanelProps> = ({
  sessionId,
  batchFileName,
  selectedNode,
  onDeselectNode,
  operations,
  stream,
  pendingMessage,
  onPendingMessageConsumed,
  isBusy,
  contextualSuggestions,
  logicProps,
  diagnosticsProps,
  healthIssueCount,
  onMappingFeedbackSuccess,
  mappingActive,
  onSwitchToDagMode,
  collapsed,
  onToggleCollapse,
}) => {
  const [activeTab, setActiveTab] = useState<ContextTab>('chat');

  // Auto-switch tabs based on selection state
  const hasSelection = !!logicProps.selection;
  useEffect(() => {
    if (hasSelection) {
      setActiveTab('logic');
    } else if (activeTab === 'logic') {
      setActiveTab('chat');
    }
  }, [hasSelection]); // eslint-disable-line

  // "Ask AI About This Mapping" handler from LogicEditorTab → switch to Chat with mapping context
  const handleAskAboutMapping = useCallback(
    (_fieldLabel: string) => {
      setActiveTab('chat');
    },
    [],
  );

  // Derive mapping context for ChatPanel from current selection
  const activeMappingContext = useMemo<MappingContext | null>(() => {
    if (!logicProps.selection || !logicProps.mappingResponse) return null;
    const sel = logicProps.selection;
    const mappingId = sel.kind === 'node'
      ? sel.node.data.mapping?.mapping_id ?? sel.node.data.mappingIds?.[0]
      : sel.edge.data?.mappingId;
    if (!mappingId) return null;
    const mapping = logicProps.mappingResponse.mappings?.find((m) => m.mapping_id === mappingId);
    if (!mapping) return null;
    return { mapping, mappingResponse: logicProps.mappingResponse };
  }, [logicProps.selection, logicProps.mappingResponse]);

  if (collapsed) {
    return (
      <Box
        sx={{
          width: 48,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1,
          gap: 1,
          bgcolor: 'background.paper',
          borderLeft: 1,
          borderColor: 'divider',
        }}
      >
        <Tooltip title="Expand panel" placement="left">
          <IconButton size="small" onClick={onToggleCollapse} sx={{ color: 'primary.main' }}>
            <ChatIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header: Back + Tabs + Collapse */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 40,
        }}
      >
        <Tooltip title="Back to DAG View">
          <IconButton size="small" onClick={onSwitchToDagMode} sx={{ ml: 0.5 }}>
            <BackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            flex: 1,
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 12, textTransform: 'none' },
          }}
        >
          <Tab
            value="chat"
            icon={<ChatIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
            label="Chat"
          />
          <Tab
            value="logic"
            icon={<LogicIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
            label="Logic"
            disabled={!hasSelection}
          />
          <Tab
            value="diagnostics"
            icon={<DiagIcon sx={{ fontSize: 14 }} />}
            iconPosition="start"
            label="Lineage"
          />
        </Tabs>

        <Tooltip title="Collapse panel">
          <IconButton size="small" onClick={onToggleCollapse} sx={{ mr: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}>
            <CollapseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tab content — full remaining height */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <ChatPanel
            sessionId={sessionId}
            batchFileName={batchFileName}
            selectedNode={selectedNode}
            onDeselectNode={onDeselectNode}
            operations={operations}
            stream={stream}
            pendingMessage={pendingMessage}
            onPendingMessageConsumed={onPendingMessageConsumed}
            isBusy={isBusy}
            hideModes
            mappingActive={mappingActive}
            activeMappingContext={activeMappingContext}
            onMappingFeedbackSuccess={onMappingFeedbackSuccess}
          />
        )}

        {/* Logic Tab */}
        {activeTab === 'logic' && (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <LogicEditorTab
              {...logicProps}
              onAskAboutMapping={handleAskAboutMapping}
            />
          </Box>
        )}

        {/* Diagnostics Tab */}
        {activeTab === 'diagnostics' && (
          <DiagnosticsTab {...diagnosticsProps} />
        )}
      </Box>
    </Box>
  );
};

export default MappingContextPanel;
